<template>
  <v-card class="order-items-table">
    <v-card-title class="toolbar white--text">
      <h2>Order Items ({{ filteredItems.length }} of {{ pagination.totalItems }})</h2>
      <v-spacer />
      <v-select
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="pending_actions"
        class="ma-0 mr-4"
        :items="orderStatus"
        label="Order Status"
        item-value="value"
        hide-details
        clearable
      />
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Search orders by user, snr, isin..."
        single-line
        hide-details
      />
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.orderStatus"

        class="ml-5"
        primary
        :items="orderStatus"
        label="Status"
        item-value="value"
        clearable
      />
      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="orderItemsPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :expand="expand"
      item-key="id"
      class="elevation-1"
      :pagination.sync="pagination"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            <span v-if="props.item.account">
              {{ props.item.account.user_id }}
            </span>
            <v-tooltip
              v-else
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="error"
                  flat
                  icon
                  v-on="on"
                >
                  <v-icon>error</v-icon>
                </v-btn>
              </template>
              <span>Error getting account information</span>
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            {{ props.item.snr }}
          </td>
          <td class="text-xs-center">
            {{ props.item.order.isin }}
          </td>
          <td class="text-xs-center">
            {{ props.item.nominal_value }} €
          </td>
          <td class="text-xs-center">
            {{ props.item.order.status.status }}
          </td>
          <td class="text-xs-center">
            {{ formatDateSmall(props.item.order.order_date) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.order.file.path ? props.item.order.file.path : '-' }}
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.created_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.created_at) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.updated_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.updated_at) }}
            </v-tooltip>
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <BaaderRowDetail
          :item="props.item"
          :user-id="props.item.account ? props.item.account.user_id : 0"
          :snr="props.item.snr"
        />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions } from 'vuex';
import { toQueryString, setQuery } from '@/helpers';
import BaaderRowDetail from '@/components/baader/shared/BaaderRowDetail';
import OrderStatusEnum from '@/enum/baader/OrderStatus';

export default {
  name: 'OrderItemsTable',
  components: {
    BaaderRowDetail,
  },
  props: {
    orderItems: {
      type: Array,
      default: () => [],
    },
    orderItemsPagination: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      search: undefined,
      selected: [],
      pagination: {
        page: this.orderItemsPagination.page,
        rowsPerPage: this.orderItemsPagination.limit,
        sortBy: 'created_at',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        orderStatus: null,
        searchResults: null,
      },
      headers: [
        {
          text: 'Id',
          align: 'left',
          value: 'id',
        },
        {
          text: 'UserId',
          align: 'left',
          value: 'account.user_id',
        },
        {
          text: 'Snr',
          align: 'left',
          value: 'snr',
        },
        {
          text: 'ISIN',
          align: 'left',
          value: 'order.isin',
        },
        {
          text: 'Amount',
          align: 'left',
          value: 'nominal_value',
        },
        {
          text: 'Status',
          align: 'left',
          value: 'order.status.status',
        },
        {
          text: 'OrderDate',
          align: 'left',
          value: 'order.order_date',
        },
        {
          text: 'File',
          align: 'left',
          value: 'order.file.path',
        },
        {
          text: 'Created at',
          value: 'created_at',
        },
        {
          text: 'Updated at',
          value: 'updated_at',
        },
      ],
    };
  },
  computed: {
    filteredItems() {
      return this.orderItems.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasStatus = false;
          let hasSearch = false;

          const { orderStatus, searchResults } = this.resultFilters;

          if (orderStatus) {
            hasStatus = item.order.status.status === orderStatus;
          }

          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasStatus || !!hasSearch) || false;
        });
      }

      return this.filteredItems;
    },

    orderStatus() {
      return Object.keys(OrderStatusEnum).map(key => ({
        text: this.formatFieldName(OrderStatusEnum[key]),
        value: OrderStatusEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.orderStatus
        || !!this.resultFilters.searchResults;
    },
  },
  watch: {
    async orderItems() {
      this.getDefaultFilters();
      await this.$nextTick();
      await this.changePage();
    },
    orderItemsPagination: {
      immediate: false,
      async handler(orderItemsPagination) {
        if (orderItemsPagination) {
          this.pagination.page = orderItemsPagination.page;
          this.pagination.rowsPerPage = orderItemsPagination.limit;
          this.pagination.totalItems = orderItemsPagination.total;
        }
      },
    },
    search: {
      immediate: false,
      handler(search, oldSearch) {
        if (search !== oldSearch) {
          setQuery(
            {
              search,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
    pagination: {
      immediate: false,
      handler(pagination, oldPagination) {
        if (!_.isEqual({
          descending: pagination.descending,
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          sortBy: pagination.sortBy,
          totalItems: pagination.totalItems,
        }, {
          descending: oldPagination.descending,
          page: oldPagination.page,
          rowsPerPage: oldPagination.rowsPerPage,
          sortBy: oldPagination.sortBy,
          totalItems: oldPagination.totalItems || 0,
        })) {
          if (pagination.sortBy) {
            this.refreshTable();
          }
        }
      },
    },
  },
  methods: {
    ...mapActions(['getOrderItems']),
    refreshTable() {
      const query = toQueryString({
        page: this.pagination.page || 1,
        limit: this.pagination.rowsPerPage || 25,
        search: this.search,
        sort: this.pagination.sortBy,
        dir: this.pagination.descending ? 'desc' : 'asc',
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getOrderItems(query);
      }, 1000);
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      if (date) {
        return this.$moment(date).format('DD/MM/YYYY');
      }

      return '-';
    },
    centsToEuros(amount) {
      amount = `${parseInt(amount, 0) / 100}`;

      return `${parseFloat(amount).toFixed(2)} €`;
    },

    /**
     * removes keys from object if value is falsy or typeof object
     */
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },
    updatePagination(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          search: this.search,
        },
        this.$route,
        this.$router,
      );
    },
    toogleSelected(props) {
      props.selected = !props.selected;
    },
    async changePage() {
      const { page, search } = this.$route.query;
      this.search = search;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
      const sentence = _.startCase(camelCaseSentence);

      if (item) {
        if (str === 'security_wallet' && item.sell_offer.asset.provider === 'baaderbank') {
          return `${_.capitalize(sentence)} / Baader Bank account`;
        }
      }

      return _.capitalize(sentence);
    },

    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },

    checkObjectKeys(item, value) {
      return JSON.stringify(item)
        .toLowerCase()
        .includes(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.order-items-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/baader/OrderItemsTable.vue